import { type UserRole } from '@mobx/interfaces';

/**
 * Решение гварда «роль выбрана» (см. RoleSelectedGuard.tsx). Вынесено в чистую функцию,
 * чтобы покрыть тестом без React/DI (roleGuardDecision.test.ts).
 */
export type RoleGuardDecision =
  // Решать рано: persist-стор ещё не гидратирован, либо токена уже нет (идёт logout / 401).
  | { type: 'wait' }
  // Роль выбрана или выбирать нечего — пропускаем в раздел.
  | { type: 'pass' }
  // Ролей для выбора больше одной, а роль не выбрана — на экран выбора роли.
  | { type: 'redirect' }
  // Доступная роль ровно одна — выбираем её автоматически, без перехода.
  | { type: 'autoSelect'; role: UserRole };

export interface RoleGuardInput {
  /** Есть ли токен основной сессии (`hasSessionToken()`). */
  hasToken: boolean;
  /** Завершилась ли гидратация RolesModule (`rolesModule.isStoreHydrated`). */
  isStoreHydrated: boolean;
  /** `rolesModule.currentRole`. */
  currentRole: UserRole | null;
  /** Роли, доступные на экране выбора (`getSelectableRoles(rolesModule.roles)`). */
  selectableRoles: UserRole[];
}

export const resolveRoleGuardDecision = ({
  hasToken,
  isStoreHydrated,
  currentRole,
  selectableRoles
}: RoleGuardInput): RoleGuardDecision => {
  // Токена нет — значит мы внутри logout()/sessionExpired(): они сами уводят на «/» или
  // check-contact. Если здесь ещё и редиректить на выбор роли, две навигации подерутся.
  // Открытие закрытой зоны без токена сюда не доходит — его раньше отсекает AuthGuard.
  if (!hasToken) return { type: 'wait' };

  // До гидрации currentRole === null даже у пользователя с выбранной ролью — ничего не решаем.
  if (!isStoreHydrated) return { type: 'wait' };

  if (currentRole) return { type: 'pass' };

  if (selectableRoles.length === 1) return { type: 'autoSelect', role: selectableRoles[0] };

  if (selectableRoles.length > 1) return { type: 'redirect' };

  // Выбирать нечего (roles пуст: userData не загрузился, либо у пользователя только роли,
  // которых нет в SUPPORTED_USER_ROLES, например ADMIN). Экран выбора роли показал бы пустой
  // список — тупик без выхода. Поэтому пропускаем, как было до гварда (поведение не меняется).
  return { type: 'pass' };
};
